import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  ISettingsRepository,
  CompanySettings,
  DocumentAccessSettings,
  DocumentCreator,
  UpdateCompanySettingsInput,
  ModuleSetting,
  UserModuleOverride,
  TicketPriceType,
  TicketElementKey,
  SidebarBrandDisplay,
  SalesDocType,
  ReleasedDocumentNumber,
  ReleasedNumbersReport,
} from '../../../interfaces/src';
import {
  parseDocumentNumber,
  buildDocumentNumber,
  findSeriesGaps,
} from '@erp/domain/numbering';
import {
  SALES_DOC_NUMBERING,
  SALES_DOC_TYPES,
  SALES_DOC_DEFAULT_PADDING,
} from './sales-numbering';
import { normalizeBrandRadius } from '@erp/config';
import { readDocumentAccessSettings, writeDocumentAccessSettings, readDocumentCreators } from './document-access-rpc';

/**
 * Plafond du scan de trous proposés à la récupération. Une reprise partielle
 * d'historique peut en ouvrir des millions ; au-delà de quelques centaines, la
 * bonne réponse n'est plus de cocher des cases mais de régler le prochain n°.
 */
const RECLAIMABLE_SCAN_LIMIT = 500;

const DEFAULT_TICKET_ORDER: TicketElementKey[] = ['companyName', 'name', 'reference', 'price', 'barcode'];
const VALID_KEYS: ReadonlySet<TicketElementKey> = new Set(DEFAULT_TICKET_ORDER);
const SIDEBAR_BRAND_DISPLAY_VALUES: ReadonlySet<SidebarBrandDisplay> = new Set(['logo_only', 'logo_with_name']);

function parseSidebarBrandDisplay(raw: unknown): SidebarBrandDisplay {
  return SIDEBAR_BRAND_DISPLAY_VALUES.has(raw as SidebarBrandDisplay)
    ? (raw as SidebarBrandDisplay)
    : 'logo_with_name';
}

function parseTicketOrder(raw: unknown): TicketElementKey[] {
  if (typeof raw !== 'string' || raw.trim() === '') return [...DEFAULT_TICKET_ORDER];
  const parts = raw.split(',').map((s) => s.trim()).filter((s) => VALID_KEYS.has(s as TicketElementKey)) as TicketElementKey[];
  const seen = new Set<TicketElementKey>();
  const ordered: TicketElementKey[] = [];
  parts.forEach((k) => { if (!seen.has(k)) { ordered.push(k); seen.add(k); } });
  DEFAULT_TICKET_ORDER.forEach((k) => { if (!seen.has(k)) ordered.push(k); });
  return ordered;
}

export class SupabaseSettingsRepository implements ISettingsRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  getDocumentAccessSettings(): Promise<DocumentAccessSettings> {
    return readDocumentAccessSettings(this.supabase);
  }
  updateDocumentAccessSettings(input: Pick<DocumentAccessSettings, 'showDocumentCreatorToTeam'>): Promise<DocumentAccessSettings> {
    return writeDocumentAccessSettings(this.supabase, input);
  }
  getDocumentCreators(family: 'sales' | 'purchases', documentIds: string[]): Promise<DocumentCreator[]> {
    return readDocumentCreators(this.supabase, family, documentIds);
  }

  /**
   * Resolve the caller's tenant_id via the `current_user_tenant_id()` RPC.
   *
   * Do NOT read it from `from('tenants').select('id').limit(1)`: platform
   * admins (is_admin()) can SELECT every tenant row through RLS, so the
   * LIMIT 1 returns an ARBITRARY tenant — not necessarily the admin's own.
   * Upserting module_settings / user_module_overrides with that wrong
   * tenant_id then fails the permissive tenant-isolation WITH CHECK, the
   * write is rejected, and the Settings toggle silently snaps back (the
   * "can't deactivate modules" bug). getCompanySettings already resolves
   * the tenant this way for exactly this reason.
   */
  private async resolveTenantId(): Promise<string> {
    type SbRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>;
    };
    const { data: tenantId, error } = await (this.supabase as unknown as SbRpc).rpc(
      'current_user_tenant_id',
      {},
    );
    if (error) throw new Error(error.message);
    if (!tenantId) throw new Error('not_in_tenant');
    return tenantId;
  }

  async getCompanySettings(): Promise<CompanySettings> {
    // Filter by current_user_tenant_id() explicitly so platform admins
    // (whose RLS lets them see every tenant's company_settings) read
    // the SAME tenant that BEFORE-INSERT triggers will write to.
    // Otherwise admin would see "tenant A's settings" while creating
    // articles under "tenant B".
    type SbAny = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: string | null; error: { message: string } | null }> };
    const { data: tenantId, error: tenantResolutionError } = await (this.supabase as unknown as SbAny).rpc(
      'current_user_tenant_id',
      {},
    );
    if (tenantResolutionError) throw tenantResolutionError;
    if (!tenantId) throw new Error('not_in_tenant');
    const { data, error } = await this.supabase
      .from('company_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('company_settings_not_found');
    return mapCompanySettings(data);
  }

  async updateCompanySettings(input: UpdateCompanySettingsInput): Promise<CompanySettings> {
    const current = await this.getCompanySettings();
    const mapped: TablesUpdate<'company_settings'> = {};
    if (input.companyName !== undefined) mapped['company_name'] = input.companyName;
    if (input.softwareName !== undefined) mapped['software_name'] = input.softwareName;
    if (input.taxId !== undefined) mapped['tax_id'] = input.taxId;
    if (input.address !== undefined) mapped['address'] = input.address;
    if (input.phone !== undefined) mapped['phone'] = input.phone;
    if (input.email !== undefined) mapped['email'] = input.email;
    if (input.logoUrl !== undefined) mapped['logo_url'] = input.logoUrl;
    if (input.logoDarkUrl !== undefined) mapped['logo_dark_url'] = input.logoDarkUrl;
    if (input.faviconUrl !== undefined) mapped['favicon_url'] = input.faviconUrl;
    if (input.logoSmallUrl !== undefined) mapped['logo_small_url'] = input.logoSmallUrl;
    // Login page (dashboard /login) — own slot, independent from logo_url.
    // Columns added in migration 20260528130000_company_settings_login_logo.sql
    // and not yet present in the stale generated types; cast through `m`.
    if (input.loginLogoUrl !== undefined) (mapped as Record<string, unknown>)['login_logo_url'] = input.loginLogoUrl;
    if (input.loginLogoDarkUrl !== undefined) (mapped as Record<string, unknown>)['login_logo_dark_url'] = input.loginLogoDarkUrl;
    // PDF documents — own logo slot, independent from logo_url (sidebar).
    // Column added in migration 20260602110000_company_settings_pdf_logo.sql.
    if (input.pdfLogoUrl !== undefined) (mapped as Record<string, unknown>)['pdf_logo_url'] = input.pdfLogoUrl;
    if (input.sidebarBrandDisplay !== undefined) mapped['sidebar_brand_display'] = input.sidebarBrandDisplay;
    if (input.primaryColor !== undefined) mapped['primary_color'] = input.primaryColor;
    if (input.borderRadius !== undefined) {
      mapped['border_radius'] = normalizeBrandRadius(input.borderRadius);
    }
    if (input.themeMode !== undefined) mapped['theme_mode'] = input.themeMode;
    if (input.themePreset !== undefined) mapped['theme_preset'] = input.themePreset;
    if (input.defaultVatRate !== undefined) mapped['default_vat_rate'] = input.defaultVatRate;
    if (input.withholdingThreshold !== undefined) mapped['withholding_threshold'] = input.withholdingThreshold;
    if (input.withholdingRate !== undefined) mapped['withholding_rate'] = input.withholdingRate;
    if (input.fiscalStampAmount !== undefined) (mapped as Record<string, unknown>)['fiscal_stamp_amount'] = input.fiscalStampAmount;
    // FODEC — colonnes ajoutées par la migration 20260907140000_fodec_sales_documents.sql,
    // absentes des types générés : cast comme les autres colonnes récentes.
    if (input.fodecEnabled !== undefined) (mapped as Record<string, unknown>)['fodec_enabled'] = input.fodecEnabled;
    if (input.fodecRate !== undefined) (mapped as Record<string, unknown>)['fodec_rate'] = input.fodecRate;
    if (input.reuseDeletedNumbers !== undefined) mapped['reuse_deleted_numbers'] = input.reuseDeletedNumbers;
    if (input.showClientCarNumber !== undefined) (mapped as Record<string, unknown>)['show_client_car_number'] = input.showClientCarNumber;
    if (input.tagline !== undefined) (mapped as Record<string, unknown>)['tagline'] = input.tagline;
    if (input.subtitle !== undefined) (mapped as Record<string, unknown>)['subtitle'] = input.subtitle;
    if (input.website !== undefined) (mapped as Record<string, unknown>)['website'] = input.website;
    if (input.fax !== undefined) (mapped as Record<string, unknown>)['fax'] = input.fax;
    if (input.rib !== undefined) (mapped as Record<string, unknown>)['rib'] = input.rib;
    if (input.bankName !== undefined) (mapped as Record<string, unknown>)['bank_name'] = input.bankName;
    if (input.socialFacebook !== undefined) (mapped as Record<string, unknown>)['social_facebook'] = input.socialFacebook;
    if (input.socialInstagram !== undefined) (mapped as Record<string, unknown>)['social_instagram'] = input.socialInstagram;
    if (input.socialLinkedin !== undefined) (mapped as Record<string, unknown>)['social_linkedin'] = input.socialLinkedin;
    if (input.ticketWidthMm !== undefined) mapped['ticket_width_mm'] = input.ticketWidthMm;
    if (input.ticketHeightMm !== undefined) mapped['ticket_height_mm'] = input.ticketHeightMm;
    if (input.ticketShowName !== undefined) mapped['ticket_show_name'] = input.ticketShowName;
    if (input.ticketShowPrice !== undefined) mapped['ticket_show_price'] = input.ticketShowPrice;
    if (input.ticketPriceType !== undefined) mapped['ticket_price_type'] = input.ticketPriceType;
    if (input.ticketShowReference !== undefined) mapped['ticket_show_reference'] = input.ticketShowReference;
    if (input.ticketShowBarcode !== undefined) mapped['ticket_show_barcode'] = input.ticketShowBarcode;
    if (input.ticketShowCompanyName !== undefined) mapped['ticket_show_company_name'] = input.ticketShowCompanyName;
    if (input.ticketCurrencySymbol !== undefined) mapped['ticket_currency_symbol'] = input.ticketCurrencySymbol;
    if (input.ticketElementOrder !== undefined) mapped['ticket_element_order'] = input.ticketElementOrder.join(',');
    // Auto-increment codes — colonnes ajoutées dans la migration, pas encore dans database.types
    const m = mapped as Record<string, unknown>;
    if (input.clientCodePrefix !== undefined) m['client_code_prefix'] = input.clientCodePrefix;
    if (input.clientCodeNext !== undefined) m['client_code_next'] = input.clientCodeNext;
    if (input.clientCodePadding !== undefined) m['client_code_padding'] = input.clientCodePadding;
    if (input.supplierCodePrefix !== undefined) m['supplier_code_prefix'] = input.supplierCodePrefix;
    if (input.supplierCodeNext !== undefined) m['supplier_code_next'] = input.supplierCodeNext;
    if (input.supplierCodePadding !== undefined) m['supplier_code_padding'] = input.supplierCodePadding;
    if (input.articleCodePrefix !== undefined) m['article_code_prefix'] = input.articleCodePrefix;
    if (input.articleCodeNext !== undefined) m['article_code_next'] = input.articleCodeNext;
    if (input.articleCodePadding !== undefined) m['article_code_padding'] = input.articleCodePadding;
    if (input.barcodePrefix !== undefined) m['barcode_prefix'] = input.barcodePrefix;
    if (input.barcodeNext !== undefined) m['barcode_next'] = input.barcodeNext;
    if (input.barcodePadding !== undefined) m['barcode_padding'] = input.barcodePadding;
    // Document numbering (sales) — colonnes ajoutées dans la migration
    // 20260612120000, pas encore dans database.types générées.
    if (input.docNumberIncludeYear !== undefined) m['doc_number_include_year'] = input.docNumberIncludeYear;
    if (input.quoteDocPrefix !== undefined) m['quote_doc_prefix'] = input.quoteDocPrefix;
    if (input.quoteDocPadding !== undefined) m['quote_doc_padding'] = input.quoteDocPadding;
    if (input.orderDocPrefix !== undefined) m['order_doc_prefix'] = input.orderDocPrefix;
    if (input.orderDocPadding !== undefined) m['order_doc_padding'] = input.orderDocPadding;
    if (input.deliveryDocPrefix !== undefined) m['delivery_doc_prefix'] = input.deliveryDocPrefix;
    if (input.deliveryDocPadding !== undefined) m['delivery_doc_padding'] = input.deliveryDocPadding;
    if (input.invoiceDocPrefix !== undefined) m['invoice_doc_prefix'] = input.invoiceDocPrefix;
    if (input.invoiceDocPadding !== undefined) m['invoice_doc_padding'] = input.invoiceDocPadding;
    if (input.creditNoteDocPrefix !== undefined) m['credit_note_doc_prefix'] = input.creditNoteDocPrefix;
    if (input.creditNoteDocPadding !== undefined) m['credit_note_doc_padding'] = input.creditNoteDocPadding;

    const { data, error } = await this.supabase
      .from('company_settings')
      .update(mapped)
      .eq('id', current.id)
      .select()
      .single();
    if (error) throw error;
    return mapCompanySettings(data);
  }

  async getDocSequenceStarts(): Promise<Record<SalesDocType, number>> {
    // peek_next_sequence_value ne consomme pas le compteur ; il renvoie le
    // prochain numéro qui sera attribué (ou 1 si aucun doc encore émis).
    // Pas encore dans les types générés → cast comme ailleurs dans ce repo.
    type SbRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: number | null; error: { message: string } | null }>;
    };
    const sb = this.supabase as unknown as SbRpc;
    const entries = await Promise.all(
      SALES_DOC_TYPES.map(async (docType) => {
        const { data } = await sb.rpc('peek_next_sequence_value', {
          seq_name: SALES_DOC_NUMBERING[docType].seq,
        });
        return [docType, Math.max(1, Number(data ?? 1))] as const;
      }),
    );
    return Object.fromEntries(entries) as Record<SalesDocType, number>;
  }

  async setDocSequenceStart(docType: SalesDocType, nextValue: number): Promise<void> {
    if (!Number.isFinite(nextValue) || nextValue < 1) {
      throw new Error('next value must be >= 1');
    }
    const seqName = SALES_DOC_NUMBERING[docType].seq;
    // reset_sequence_value(seq, X) pose le prochain numéro = X directement,
    // à la hausse comme à la baisse (l'opérateur règle la valeur en phase de
    // dev). La sécurité légale est conservée à la création : getNextNumber
    // re-synchronise au-delà du plus grand numéro existant, donc une valeur
    // basse ne peut jamais dupliquer un document — elle ne « prend » que s'il
    // n'existe aucun document dans cette plage.
    const { error } = await this.supabase.rpc('reset_sequence_value', {
      seq_name: seqName,
      new_value: Math.trunc(nextValue),
    });
    if (error) throw new Error(error.message);
  }

  /**
   * Résout la série courante d'un type de document : préfixe, année, longueur.
   * Même source que la numérotation (company_settings), pour que l'écran de
   * réglage et l'attribution des numéros ne divergent jamais.
   */
  private async resolveSeries(docType: SalesDocType): Promise<{
    prefix: string;
    year: number | null;
    padding: number;
    likePattern: string;
  }> {
    const meta = SALES_DOC_NUMBERING[docType];
    const settings = await this.getCompanySettings();
    const prefixByType: Record<SalesDocType, string> = {
      quote: settings.quoteDocPrefix,
      order: settings.orderDocPrefix,
      delivery: settings.deliveryDocPrefix,
      invoice: settings.invoiceDocPrefix,
      credit_note: settings.creditNoteDocPrefix,
    };
    const paddingByType: Record<SalesDocType, number> = {
      quote: settings.quoteDocPadding,
      order: settings.orderDocPadding,
      delivery: settings.deliveryDocPadding,
      invoice: settings.invoiceDocPadding,
      credit_note: settings.creditNoteDocPadding,
    };
    const prefix = prefixByType[docType] || meta.defaultPrefix;
    const year = settings.docNumberIncludeYear ? new Date().getFullYear() : null;
    return {
      prefix,
      year,
      padding: paddingByType[docType] || SALES_DOC_DEFAULT_PADDING,
      likePattern: year == null ? `${prefix}-%` : `${prefix}-${year}-%`,
    };
  }

  /** Les numéros de la série courante réellement portés par un document. */
  private async readSeriesValues(
    docType: SalesDocType,
    series: { prefix: string; year: number | null; likePattern: string },
  ): Promise<number[]> {
    const { data } = await this.supabase
      .from('authorized_sales_documents' as 'sales_documents')
      .select('number')
      .eq('doc_type', docType)
      .like('number', series.likePattern);
    return ((data ?? []) as Array<Record<string, unknown>>)
      .map((r) => parseDocumentNumber(r['number'] as string))
      .filter(
        (p): p is NonNullable<typeof p> =>
          p !== null && p.year === series.year && p.prefix === series.prefix,
      )
      .map((p) => p.value);
  }

  async getReleasedNumbersReport(docType: SalesDocType): Promise<ReleasedNumbersReport> {
    const series = await this.resolveSeries(docType);
    const existingValues = await this.readSeriesValues(docType, series);
    const existing = new Set(existingValues);

    // `as any` : released_document_numbers (migration 20260818120000) pas encore
    // dans database.types.ts — même cast qu'ailleurs dans ce repo.
    let rows: Array<Record<string, unknown>> = [];
    try {
      // Filtre tenant explicite : RLS expose tous les tenants à un admin
      // plateforme, qui verrait sinon le registre d'un autre client.
      const tenantId = await this.resolveTenantId();
      const { data } = await (this.supabase as any)
        .from('released_document_numbers')
        .select('id, number, released_at')
        .eq('tenant_id', tenantId)
        .eq('doc_type', docType)
        .like('number', series.likePattern)
        .order('number', { ascending: true });
      rows = (data ?? []) as Array<Record<string, unknown>>;
    } catch {
      // Migration pas encore appliquée : registre vide, l'écran reste utilisable.
      rows = [];
    }

    const pending: ReleasedDocumentNumber[] = rows
      .map((r) => {
        const number = r['number'] as string;
        const parsed = parseDocumentNumber(number);
        return parsed && parsed.year === series.year && parsed.prefix === series.prefix
          ? {
            id: r['id'] as string,
            number,
            value: parsed.value,
            releasedAt: (r['released_at'] as string | null) ?? '',
          }
          : null;
      })
      .filter((e): e is ReleasedDocumentNumber => e !== null)
      // Un numéro repris entre-temps n'attend plus rien : même filtre que
      // findNextNumber, pour que l'écran montre ce qui va VRAIMENT revenir.
      .filter((e) => !existing.has(e.value))
      .sort((a, b) => a.value - b.value);

    // Les trous constatés, moins ceux déjà consignés : proposer d'inscrire un
    // numéro qui reviendra de toute façon n'aurait aucun sens.
    const consigned = new Set(pending.map((e) => e.value));
    const gaps = findSeriesGaps(existingValues, RECLAIMABLE_SCAN_LIMIT);
    const reclaimableValues = gaps.values.filter((v) => !consigned.has(v));

    // Un numéro consigné n'est un TROU que s'il tombe à l'intérieur des bornes :
    // celui d'un dernier document supprimé est au-dessus du max et n'a jamais
    // compté dans `gaps.total`. Le déduire quand même ferait afficher un total
    // trop bas — d'où le test de position plutôt qu'un simple `consigned.size`.
    const consignedInsideRange = pending.filter(
      (e) => gaps.from != null && gaps.to != null && e.value > gaps.from && e.value < gaps.to,
    ).length;

    return {
      docType,
      pending,
      reclaimable: reclaimableValues.map((v) =>
        buildDocumentNumber(series.prefix, series.year, v, series.padding),
      ),
      reclaimableTotal: Math.max(0, gaps.total - consignedInsideRange),
      firstNumber:
        gaps.from == null
          ? null
          : buildDocumentNumber(series.prefix, series.year, gaps.from, series.padding),
      lastNumber:
        gaps.to == null
          ? null
          : buildDocumentNumber(series.prefix, series.year, gaps.to, series.padding),
      skippedRuns: gaps.skippedRuns,
      skippedValues: gaps.skippedValues,
    };
  }

  async reclaimReleasedNumbers(docType: SalesDocType, numbers: string[]): Promise<number> {
    if (numbers.length === 0) return 0;
    const series = await this.resolveSeries(docType);
    const existing = new Set(await this.readSeriesValues(docType, series));

    // Deux refus, tous deux silencieux : un numéro d'une AUTRE série (l'écran a
    // pu être laissé ouvert pendant un changement de préfixe) et un numéro
    // qu'un document porte encore — l'inscrire fabriquerait un doublon annoncé.
    const accepted = [...new Set(numbers)].filter((number) => {
      const parsed = parseDocumentNumber(number);
      return (
        parsed !== null
        && parsed.prefix === series.prefix
        && parsed.year === series.year
        && !existing.has(parsed.value)
      );
    });
    if (accepted.length === 0) return 0;

    // `as any` : voir getReleasedNumbersReport. Ici l'erreur REMONTE — c'est une
    // action explicite de l'utilisateur, qui doit savoir si elle a échoué.
    const { error } = await (this.supabase as any)
      .from('released_document_numbers')
      .upsert(
        accepted.map((number) => ({ doc_type: docType, number })),
        { onConflict: 'tenant_id,doc_type,number', ignoreDuplicates: true },
      );
    if (error) throw new Error(error.message);
    return accepted.length;
  }

  async clearReleasedNumbers(docType: SalesDocType): Promise<void> {
    // Portée : ce type de document, toutes séries confondues (préfixes et
    // années antérieurs compris). « Repartir de zéro » doit vider la table, pas
    // laisser derrière des numéros d'un ancien préfixe qui ressurgiraient le
    // jour où on y revient.
    //
    // Le filtre tenant est ici une SÉCURITÉ, pas une optimisation : RLS laisse
    // un admin plateforme écrire chez tous les tenants, et un DELETE sans borne
    // viderait le registre de tout le monde d'un seul clic.
    const tenantId = await this.resolveTenantId();
    const { error } = await (this.supabase as any)
      .from('released_document_numbers')
      .delete()
      .eq('tenant_id', tenantId)
      .eq('doc_type', docType);
    if (error) throw new Error(error.message);
  }

  async getModuleSettings(): Promise<ModuleSetting[]> {
    const { data, error } = await this.supabase.from('module_settings').select('*');
    if (error) throw error;
    return (data ?? []).map((r) => ({
      moduleKey: r.module_key,
      isEnabled: r.is_enabled ?? false,
    }));
  }

  async updateModuleSetting(moduleKey: string, isEnabled: boolean): Promise<ModuleSetting> {
    // Resolve the caller's tenant_id so the upsert composite key
    // (tenant_id, module_key) matches the schema's PRIMARY KEY. We
    // upsert (not just update) because legacy tenants pre-dating
    // 20260426060000_backfill_module_settings might still be missing
    // a row for some keys; without an INSERT path the toggle would
    // silently no-op.
    //
    // module_settings isn't in the auto-generated database.types.ts with
    // its tenant_id column yet (generated before the multi-tenant
    // migration), so we cast through `unknown` for the upsert builder.
    type SbAny = { from: (t: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const tenantId = await this.resolveTenantId();

    const upsertRes = await (sb.from('module_settings') as {
      upsert: (
        row: { tenant_id: string; module_key: string; is_enabled: boolean },
        opts: { onConflict: string },
      ) => {
        select: () => {
          single: () => Promise<{
            data: { module_key: string; is_enabled: boolean | null } | null;
            error: { message: string } | null;
          }>;
        };
      };
    }).upsert(
      { tenant_id: tenantId, module_key: moduleKey, is_enabled: isEnabled },
      { onConflict: 'tenant_id,module_key' },
    ).select().single();

    if (upsertRes.error) throw new Error(upsertRes.error.message);
    if (!upsertRes.data) throw new Error('upsert_returned_no_row');
    return {
      moduleKey: upsertRes.data.module_key,
      isEnabled: upsertRes.data.is_enabled ?? false,
    };
  }

  // user_module_overrides isn't in the auto-generated database.types.ts
  // yet (table added after the last gen run), so we cast through unknown
  // for the typed-builder path. Same idiom as updateModuleSetting above.

  async getUserModuleOverrides(): Promise<UserModuleOverride[]> {
    type SbAny = { from: (t: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const res = await (sb.from('user_module_overrides') as {
      select: (cols: string) => Promise<{
        data: { user_id: string; module_key: string; is_enabled: boolean | null }[] | null;
        error: { message: string } | null;
      }>;
    }).select('user_id, module_key, is_enabled');
    if (res.error) throw new Error(res.error.message);
    return (res.data ?? []).map((r) => ({
      userId: r.user_id,
      moduleKey: r.module_key,
      isEnabled: r.is_enabled ?? false,
    }));
  }

  async setUserModuleOverride(
    userId: string,
    moduleKey: string,
    isEnabled: boolean,
  ): Promise<UserModuleOverride> {
    // Resolve the caller's tenant_id so the upsert composite key
    // (tenant_id, user_id, module_key) matches the schema's PRIMARY KEY.
    type SbAny = { from: (t: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const tenantId = await this.resolveTenantId();

    const upsertRes = await (sb.from('user_module_overrides') as {
      upsert: (
        row: { tenant_id: string; user_id: string; module_key: string; is_enabled: boolean },
        opts: { onConflict: string },
      ) => {
        select: () => {
          single: () => Promise<{
            data: { user_id: string; module_key: string; is_enabled: boolean | null } | null;
            error: { message: string } | null;
          }>;
        };
      };
    }).upsert(
      { tenant_id: tenantId, user_id: userId, module_key: moduleKey, is_enabled: isEnabled },
      { onConflict: 'tenant_id,user_id,module_key' },
    ).select().single();

    if (upsertRes.error) throw new Error(upsertRes.error.message);
    if (!upsertRes.data) throw new Error('upsert_returned_no_row');
    return {
      userId: upsertRes.data.user_id,
      moduleKey: upsertRes.data.module_key,
      isEnabled: upsertRes.data.is_enabled ?? false,
    };
  }

  async removeUserModuleOverride(userId: string, moduleKey: string): Promise<void> {
    type SbAny = { from: (t: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const tenantId = await this.resolveTenantId();

    const delRes = await (sb.from('user_module_overrides') as {
      delete: () => {
        eq: (col: string, val: string) => {
          eq: (col: string, val: string) => {
            eq: (col: string, val: string) => Promise<{ error: { message: string } | null }>;
          };
        };
      };
    })
      .delete()
      .eq('tenant_id', tenantId)
      .eq('user_id', userId)
      .eq('module_key', moduleKey);
    if (delRes.error) throw new Error(delRes.error.message);
  }
}

function mapCompanySettings(row: Record<string, unknown>): CompanySettings {
  return {
    id: row['id'] as string,
    companyName: row['company_name'] as string,
    softwareName: (row['software_name'] as string) ?? null,
    taxId: (row['tax_id'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    logoUrl: (row['logo_url'] as string) ?? null,
    logoDarkUrl: (row['logo_dark_url'] as string) ?? null,
    faviconUrl: (row['favicon_url'] as string) ?? null,
    logoSmallUrl: (row['logo_small_url'] as string) ?? null,
    pdfLogoUrl: (row['pdf_logo_url'] as string) ?? null,
    loginLogoUrl: (row['login_logo_url'] as string) ?? null,
    loginLogoDarkUrl: (row['login_logo_dark_url'] as string) ?? null,
    sidebarBrandDisplay: parseSidebarBrandDisplay(row['sidebar_brand_display']),
    primaryColor: (row['primary_color'] as string) ?? null,
    borderRadius: normalizeBrandRadius(row['border_radius']),
    themeMode: (row['theme_mode'] as string) ?? null,
    themePreset: (row['theme_preset'] as string) ?? null,
    defaultVatRate: (row['default_vat_rate'] as number) ?? 19,
    withholdingThreshold: (row['withholding_threshold'] as number) ?? 1000,
    withholdingRate: (row['withholding_rate'] as number) ?? 1,
    fiscalStampAmount: Number(row['fiscal_stamp_amount'] ?? 1),
    fodecEnabled: (row['fodec_enabled'] as boolean) ?? false,
    fodecRate: Number(row['fodec_rate'] ?? 1),
    defaultCurrency: (row['default_currency'] as string) ?? 'TND',
    reuseDeletedNumbers: (row['reuse_deleted_numbers'] as boolean) ?? false,
    showClientCarNumber: (row['show_client_car_number'] as boolean) ?? true,
    tagline: (row['tagline'] as string) ?? null,
    subtitle: (row['subtitle'] as string) ?? null,
    website: (row['website'] as string) ?? null,
    fax: (row['fax'] as string) ?? null,
    rib: (row['rib'] as string) ?? null,
    bankName: (row['bank_name'] as string) ?? null,
    socialFacebook: (row['social_facebook'] as string) ?? null,
    socialInstagram: (row['social_instagram'] as string) ?? null,
    socialLinkedin: (row['social_linkedin'] as string) ?? null,
    ticketWidthMm: Number(row['ticket_width_mm'] ?? 40),
    ticketHeightMm: Number(row['ticket_height_mm'] ?? 20),
    ticketShowName: (row['ticket_show_name'] as boolean) ?? true,
    ticketShowPrice: (row['ticket_show_price'] as boolean) ?? true,
    ticketPriceType: ((row['ticket_price_type'] as TicketPriceType) ?? 'ttc'),
    ticketShowReference: (row['ticket_show_reference'] as boolean) ?? true,
    ticketShowBarcode: (row['ticket_show_barcode'] as boolean) ?? true,
    ticketShowCompanyName: (row['ticket_show_company_name'] as boolean) ?? false,
    ticketCurrencySymbol: (row['ticket_currency_symbol'] as string) ?? 'DT',
    ticketElementOrder: parseTicketOrder(row['ticket_element_order']),
    clientCodePrefix: (row['client_code_prefix'] as string) ?? 'CLI',
    clientCodeNext: Number(row['client_code_next'] ?? 1),
    clientCodePadding: Number(row['client_code_padding'] ?? 4),
    supplierCodePrefix: (row['supplier_code_prefix'] as string) ?? 'FRN',
    supplierCodeNext: Number(row['supplier_code_next'] ?? 1),
    supplierCodePadding: Number(row['supplier_code_padding'] ?? 4),
    articleCodePrefix: (row['article_code_prefix'] as string) ?? 'ART',
    articleCodeNext: Number(row['article_code_next'] ?? 1),
    articleCodePadding: Number(row['article_code_padding'] ?? 4),
    barcodePrefix: (row['barcode_prefix'] as string) ?? '',
    barcodeNext: Number(row['barcode_next'] ?? 1),
    barcodePadding: Number(row['barcode_padding'] ?? 8),
    docNumberIncludeYear: (row['doc_number_include_year'] as boolean) ?? true,
    quoteDocPrefix: (row['quote_doc_prefix'] as string) ?? 'DEV',
    quoteDocPadding: Number(row['quote_doc_padding'] ?? 4),
    orderDocPrefix: (row['order_doc_prefix'] as string) ?? 'CMD',
    orderDocPadding: Number(row['order_doc_padding'] ?? 4),
    deliveryDocPrefix: (row['delivery_doc_prefix'] as string) ?? 'BL',
    deliveryDocPadding: Number(row['delivery_doc_padding'] ?? 4),
    invoiceDocPrefix: (row['invoice_doc_prefix'] as string) ?? 'FAC',
    invoiceDocPadding: Number(row['invoice_doc_padding'] ?? 4),
    creditNoteDocPrefix: (row['credit_note_doc_prefix'] as string) ?? 'AV',
    creditNoteDocPadding: Number(row['credit_note_doc_padding'] ?? 4),
  };
}
